/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useState } from "react";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import LoadingIndicator from "../components/LoadingIndicator";
import Title from "../components/ui/Title";
import Stack from "../components/ui/Stack";
import Note from "../components/ui/Note";
import Icon from "../components/ui/Icon";
import OrderDetails from "../components/booking/OrderDetails";
import ClientAuth from "../components/booking/ClientAuth";
import DetailsForm from "../components/booking/DetailsForm";
import WaitingListDueNow from "../components/waiting-list/WaitingListDueNow";
import usePluginUtils from "../hooks/usePluginUtils";
import { useAuth } from "../hooks/useAuth";
import { useBooking } from "../hooks/useBooking";
import { useBookingSummary } from "../hooks/useBookingSummary";
import { useJoinWaitingList } from "../hooks/useJoinWaitingList";
import { useAuthSuccessHandler } from "../hooks/useAuthSuccessHandler";
import { useClientTerms } from "../lib/react-query/useClientTerms";

const WaitingListDetails = () => {
  const { selectedServices, selectedProvider } = useBooking();
  const { client } = useAuth();
  const { isPluginEnabled } = usePluginUtils();
  const clientLoginEnabled = isPluginEnabled("client_login");
  const depositEnabled = isPluginEnabled("deposit_paid_events");
  const { total, taxes, currency, deposit } = useBookingSummary();
  const { handleJoin, isPending } = useJoinWaitingList();
  const { data: terms, isLoading: termsLoading } = useClientTerms();

  const form = useForm({
    defaultValues: {
      name: client?.name ?? "",
      email: client?.email ?? "",
      phone: client?.phone ?? "",
    },
  });

  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);
  const handleSubmitForm = (data) => {
    const clientData =
      client && clientLoginEnabled ? null : { name: data.name, email: data.email, phone: data.phone };

    const handleError = (...args) => {
      setIsAutoSubmitting(false);
      form.setError(...args);
    };

    handleJoin(clientData, handleError);
  };

  const handleAuthSuccess = useAuthSuccessHandler({
    form,
    onSubmit: handleSubmitForm,
    setIsAutoSubmitting,
  });

  if (termsLoading) {
    return (
      <Container>
        <NavigationHeader location back="step" />
        <LoadingIndicator />
      </Container>
    );
  }

  return (
    <Container>
      <NavigationHeader location back="step" />
      <Stack $gap="1.5rem">
        <Title size="h2">Waiting list details</Title>
        <Stack $gap="2rem">
          <Stack $gap="0">
            <Note variant="filled" icon={<Icon.InfoCircle size={Icon.SIZE.XS} color="primary" />} text="You're requesting a spot on the waiting list — nothing is booked or charged yet. If a slot opens on this day we'll email you, and you can book it as long as no one takes it first." />
            <OrderDetails
              showTimeCard
              timeLabel="Any open time slot"
              canEdit
              services={selectedServices}
              provider={selectedProvider}
              price={total}
              taxes={taxes}
              currency={currency}
              deposit={depositEnabled ? deposit : null}
            >
              {total > 0 && (
                <WaitingListDueNow currency={currency} />
              )}
            </OrderDetails>
          </Stack>
          {clientLoginEnabled && !client && <ClientAuth onAuthSuccess={handleAuthSuccess} />}
          {(client || !clientLoginEnabled) && (
            <DetailsForm
              form={form}
              terms={terms}
              onSubmit={handleSubmitForm}
              submitTitle="Join the waiting list"
              isSubmitting={isPending || isAutoSubmitting}
            />
          )}
        </Stack>
      </Stack>
    </Container>
  );
};

export default WaitingListDetails;
